import { Badge, Button, Flex, rem } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { TextField } from './TextField';
import { FieldContainer, PencilWrapper, TextHeading } from './../styled';
import {
	FormField,
	FormFieldListValue,
} from '@dealstack/frontend/shared/components/Documents/FormFields/types';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ListField } from '@dealstack/frontend/shared/components/Documents/FormFields/ViewField/ListField';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2.tsx';
import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	field: FormField & { value: unknown };
	setEditFieldKey: (fieldKey: string, action?: string) => void;
}

export const ViewField = ({ field, setEditFieldKey }: Props) => {
	const tCommonUi = useTranslationCommonUi();

	switch (field.type) {
		case 'date':
			return (
				<TextField
					fieldKey={field.fieldKey}
					readOnly={field.readOnly as boolean}
					value={new Date(field.value as string).toUTCString()}
					label={field.label}
					setEditFieldKey={() => setEditFieldKey(field.fieldKey as string, field.action)}
				/>
			);

		case 'text':
		case 'select':
			return (
				<TextField
					fieldKey={field.fieldKey as string}
					readOnly={field.readOnly as boolean}
					value={field.value as string}
					label={field.label}
					setEditFieldKey={() => setEditFieldKey(field.fieldKey as string, field.action)}
				/>
			);

		case 'multiSelect':
			return (
				<FieldContainer
					data-testid={makeComplexDataTestId(
						DOCUMENT_LIBRARY_ELEMENT_ID.FIELD,
						field.fieldKey as string
					)}
				>
					<Flex justify="space-between" align="center" gap="xs" w="100%">
						<TextHeading>{field.label}</TextHeading>
						{!field.readOnly && (
							<PencilWrapper
								data-testid={makeComplexDataTestId(
									DOCUMENT_LIBRARY_ELEMENT_ID.FIELD_EDIT_BUTTON,
									field.fieldKey as string
								)}
								onClick={() =>
									setEditFieldKey(field.fieldKey as string, field.action)
								}
							>
								<Icon variant="pencil-edit" size="xl" />
							</PencilWrapper>
						)}
					</Flex>

					<Flex gap={rem(8)} wrap="wrap">
						{(field.value as string[])?.map((v, i) => (
							<Badge
								key={`${v}_${i}`}
								size="xs"
								variant="outline"
								color="gray.4"
								c="dark"
							>
								<TextV2 variant="sans/11/400" center noWrap>
									{v}
								</TextV2>
							</Badge>
						))}
					</Flex>
				</FieldContainer>
			);

		case 'boolean':
			return (
				<FieldContainer
					data-testid={makeComplexDataTestId(
						DOCUMENT_LIBRARY_ELEMENT_ID.FIELD,
						field.fieldKey as string
					)}
				>
					<TextHeading>{field.label}</TextHeading>

					<Flex justify="center" align="center" gap="xs">
						{!field.readOnly && (
							<PencilWrapper
								data-testid={makeComplexDataTestId(
									DOCUMENT_LIBRARY_ELEMENT_ID.FIELD_EDIT_BUTTON,
									field.fieldKey as string
								)}
								onClick={() =>
									setEditFieldKey(field.fieldKey as string, field.action)
								}
							>
								<Icon variant="pencil-edit" size="xl" />
							</PencilWrapper>
						)}
						<Flex gap="0.5rem" wrap="wrap">
							<Badge
								c={field.value ? 'orange.6' : 'teal.8'}
								color={field.value ? 'orange.6' : 'teal.8'}
								fw={400}
								variant="outline"
								size="md"
							>
								{field.value
									? tCommonUi('classifier.badge.false')
									: tCommonUi('classifier.badge.true')}
							</Badge>
						</Flex>
					</Flex>
				</FieldContainer>
			);

		case 'list':
			return (
				<FieldContainer
					data-testid={makeComplexDataTestId(
						DOCUMENT_LIBRARY_ELEMENT_ID.FIELD,
						field.fieldKey as string
					)}
				>
					<Flex justify="space-between" w="100%" align="center">
						<TextHeading>{field.label}</TextHeading>
						{!field.readOnly && (
							<PencilWrapper
								data-testid={makeComplexDataTestId(
									DOCUMENT_LIBRARY_ELEMENT_ID.FIELD_EDIT_BUTTON,
									field.fieldKey as string
								)}
								onClick={() =>
									setEditFieldKey(field.fieldKey as string, field.action)
								}
							>
								<Icon variant="pencil-edit" size="xl" />
							</PencilWrapper>
						)}
					</Flex>
					{Array.isArray(field.value) && (
						<>
							{field.value.slice(0, 4).map((item: FormFieldListValue, index) => (
								<ListField item={item} key={`${field.fieldKey}_${index}`} />
							))}
							{field.action && field.value.length > 4 && (
								<Button
									data-testid={makeComplexDataTestId(
										DOCUMENT_LIBRARY_ELEMENT_ID.FIELD_ACTION_BUTTON,
										field.fieldKey as string
									)}
									styles={{ root: { padding: '0px' } }}
									variant="white"
									onClick={() =>
										setEditFieldKey(field.fieldKey as string, field.action)
									}
								>
									{field.actionLabel}
								</Button>
							)}
						</>
					)}
				</FieldContainer>
			);

		default:
			return null;
	}
};
